import { Select, MenuItem, FormControl, InputLabel, Chip, FormHelperText, Box } from '@mui/material'
import type { FieldMeta } from '@crm/shared'

interface MultiSelectFieldProps {
  field: FieldMeta
  value: unknown
  mode: 'view' | 'edit'
  onChange?: (value: unknown) => void
  error?: string
  disabled?: boolean
  hideLabel?: boolean
}

function getOptions(field: FieldMeta): Array<{ value: string; label: string }> {
  if (!field.options) return []
  try {
    const parsed = JSON.parse(field.options as unknown as string)
    return parsed.options ?? parsed
  } catch {
    return []
  }
}

export function MultiSelectField({ field, value, mode, onChange, error, disabled, hideLabel }: MultiSelectFieldProps) {
  const options = getOptions(field)
  const arr: string[] = Array.isArray(value) ? value.map(String) : value != null ? [String(value)] : []

  if (mode === 'view') {
    if (arr.length === 0) return <span />
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {arr.map((v) => {
          const opt = options.find((o) => o.value === v)
          return <Chip key={v} label={opt?.label ?? v} size="small" variant="outlined" />
        })}
      </Box>
    )
  }

  return (
    <FormControl size="small" fullWidth error={!!error} required={!!field.is_required} disabled={disabled}>
      {!hideLabel && <InputLabel>{field.label}</InputLabel>}
      <Select
        multiple
        value={arr}
        onChange={(e) => onChange?.(e.target.value)}
        label={hideLabel ? undefined : field.label}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(selected as string[]).map((v) => {
              const opt = options.find((o) => o.value === v)
              return <Chip key={v} label={opt?.label ?? v} size="small" />
            })}
          </Box>
        )}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  )
}
